'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Copy, Check, LogIn, Loader2, AlertCircle } from 'lucide-react';

type LoginFields = {
  email: string;
  password: string;
  rememberMe: boolean;
};

const DEMO = { email: 'marcus.reyes@cryptofaucet.app', password: 'Faucet2026!' };

export default function LoginForm() {
  const router = useRouter();
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFields>({ defaultValues: { rememberMe: false } });

  const copyField = (field: 'email' | 'password') => {
    navigator.clipboard.writeText(DEMO[field]);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const fillDemo = () => {
    setValue('email', DEMO.email);
    setValue('password', DEMO.password);
  };

  // Backend integration point: POST /api/auth/login
  const onSubmit = async (data: LoginFields) => {
    setServerError('');
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));

    if (data.email === DEMO.email && data.password === DEMO.password) {
      router.push('/earnings-dashboard');
    } else {
      setServerError('Invalid credentials — use the demo account below to sign in.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-1">Welcome back</h2>
        <p className="text-sm text-muted-foreground">Sign in to your CryptoFaucet account</p>
      </div>

      {serverError && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          {serverError}
        </div>
      )}

      {/* Email */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="login-email" className="text-sm font-semibold text-foreground">
          Email address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          className="input-field"
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && (
          <p className="text-xs text-danger mt-0.5">{errors.email.message}</p>
        )}
      </div>

      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="login-password" className="text-sm font-semibold text-foreground">
          Password
        </label>
        <div className="relative">
          <input
            id="login-password"
            type={showPass ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="input-field pr-11"
            {...register('password', { required: 'Password is required' })}
          />
          <button
            type="button"
            onClick={() => setShowPass((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPass ? 'Hide password' : 'Show password'}
          >
            {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-danger mt-0.5">{errors.password.message}</p>
        )}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          id="remember-me"
          type="checkbox"
          className="w-4 h-4 rounded border-input accent-primary"
          {...register('rememberMe')}
        />
        <label htmlFor="remember-me" className="text-sm text-muted-foreground select-none cursor-pointer">
          Remember me for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary flex items-center justify-center gap-2 w-full py-3 text-base"
        style={{ minHeight: '48px' }}
      >
        {loading ? (
          <>
            <Loader2 size={17} className="animate-spin" />
            Signing in…
          </>
        ) : (
          <>
            <LogIn size={17} />
            Sign In
          </>
        )}
      </button>

      {/* Demo credentials */}
      <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide">Demo Account</p>
          <button
            type="button"
            onClick={fillDemo}
            className="text-xs font-semibold text-primary hover:text-sky-700 transition-colors"
          >
            Auto-fill
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {(['email', 'password'] as const).map((field) => (
            <div key={`demo-${field}`} className="flex items-center justify-between gap-2 bg-white rounded-xl px-3 py-2 border border-sky-100">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground capitalize">{field}</p>
                <p className="text-xs font-mono font-semibold text-foreground truncate">{DEMO[field]}</p>
              </div>
              <button
                type="button"
                onClick={() => copyField(field)}
                className="shrink-0 p-1.5 rounded-lg hover:bg-sky-100 text-sky-600 transition-colors"
                aria-label={`Copy ${field}`}
              >
                {copiedField === field ? <Check size={13} className="text-success" /> : <Copy size={13} />}
              </button>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}